/**
 * Every status and kind is an enum. Free strings are never used for these.
 * See plan.md §4 (Types) and CLAUDE.md.
 */
/** Lifecycle of a room and the game inside it. plan.md §7, Phase 10. */
export var GamePhase;
(function (GamePhase) {
    GamePhase["Lobby"] = "lobby";
    GamePhase["TargetAssignment"] = "target_assignment";
    GamePhase["DecisionSetup"] = "decision_setup";
    GamePhase["TurnOrderCreation"] = "turn_order_creation";
    GamePhase["TurnActive"] = "turn_active";
    GamePhase["ResponsesLocked"] = "responses_locked";
    GamePhase["Reveal"] = "reveal";
    GamePhase["QueueReconciliation"] = "queue_reconciliation";
    GamePhase["GameFinished"] = "game_finished";
    GamePhase["ResultsFinalized"] = "results_finalized";
    /** Single terminal state: host cancellation or the 2-hour expiry. */
    GamePhase["RoomClosed"] = "room_closed";
})(GamePhase || (GamePhase = {}));
/** A turn is invalid when the target never chose. plan.md §1.7. */
export var TurnOutcome;
(function (TurnOutcome) {
    TurnOutcome["Valid"] = "valid";
    TurnOutcome["Invalid"] = "invalid";
})(TurnOutcome || (TurnOutcome = {}));
/** Set by an administrator. The only classification a suggestion carries. */
export var SpiceLevel;
(function (SpiceLevel) {
    SpiceLevel[SpiceLevel["Mild"] = 1] = "Mild";
    SpiceLevel[SpiceLevel["Medium"] = 2] = "Medium";
    SpiceLevel[SpiceLevel["Hot"] = 3] = "Hot";
})(SpiceLevel || (SpiceLevel = {}));
/** Whether a curated suggestion may currently be delivered. */
export var ContentStatus;
(function (ContentStatus) {
    ContentStatus["Active"] = "active";
    ContentStatus["Inactive"] = "inactive";
})(ContentStatus || (ContentStatus = {}));
/**
 * Where a proposed suggestion is in review. Separate from `ContentStatus`
 * because it describes a different table and a different question: not "may
 * players see this" — a draft never reaches players at all — but "has anyone
 * decided about it yet".
 */
export var DraftStatus;
(function (DraftStatus) {
    DraftStatus["Pending"] = "pending";
    DraftStatus["Accepted"] = "accepted";
    DraftStatus["Rejected"] = "rejected";
})(DraftStatus || (DraftStatus = {}));
/** Which of the two option fields of a decision. */
export var OptionSide;
(function (OptionSide) {
    OptionSide["A"] = "a";
    OptionSide["B"] = "b";
})(OptionSide || (OptionSide = {}));
export var UserRole;
(function (UserRole) {
    UserRole["Player"] = "player";
    UserRole["Admin"] = "admin";
})(UserRole || (UserRole = {}));
/**
 * Carried by the typed WebSocket error event, and mirrored by HTTP status
 * codes on the REST surface. plan.md §5.3.
 */
export var ErrorCode;
(function (ErrorCode) {
    ErrorCode["Validation"] = "validation";
    ErrorCode["Unauthenticated"] = "unauthenticated";
    ErrorCode["Forbidden"] = "forbidden";
    ErrorCode["NotFound"] = "not_found";
    ErrorCode["Conflict"] = "conflict";
    ErrorCode["RateLimited"] = "rate_limited";
    ErrorCode["Maintenance"] = "maintenance";
    ErrorCode["Internal"] = "internal";
    ErrorCode["RoomNotFound"] = "room_not_found";
    ErrorCode["RoomFull"] = "room_full";
    ErrorCode["RoomBanned"] = "room_banned";
    ErrorCode["GameInProgress"] = "game_in_progress";
    ErrorCode["NameTaken"] = "name_taken";
    ErrorCode["NotHost"] = "not_host";
    ErrorCode["WrongPhase"] = "wrong_phase";
    ErrorCode["AlreadySubmitted"] = "already_submitted";
    ErrorCode["DeadlinePassed"] = "deadline_passed";
    ErrorCode["StaleVersion"] = "stale_version";
    ErrorCode["OpenedElsewhere"] = "opened_elsewhere";
})(ErrorCode || (ErrorCode = {}));
/** Client → server commands over the WebSocket. */
export var WsCommand;
(function (WsCommand) {
    WsCommand["Join"] = "join";
    WsCommand["Leave"] = "leave";
    WsCommand["Kick"] = "kick";
    WsCommand["TransferHost"] = "transfer_host";
    WsCommand["Cancel"] = "cancel";
    WsCommand["Snapshot"] = "snapshot";
    WsCommand["StartGame"] = "start_game";
    WsCommand["RequestSuggestion"] = "request_suggestion";
    WsCommand["SubmitDecision"] = "submit_decision";
    WsCommand["Choose"] = "choose";
    WsCommand["Guess"] = "guess";
    WsCommand["AdvanceReveal"] = "advance_reveal";
    WsCommand["CloseResults"] = "close_results";
    WsCommand["React"] = "react";
})(WsCommand || (WsCommand = {}));
/** Server → client events over the WebSocket. */
export var WsEvent;
(function (WsEvent) {
    WsEvent["RoomState"] = "room_state";
    WsEvent["PlayerJoined"] = "player_joined";
    WsEvent["PlayerLeft"] = "player_left";
    WsEvent["PlayerKicked"] = "player_kicked";
    WsEvent["HostChanged"] = "host_changed";
    WsEvent["RoomCancelled"] = "room_cancelled";
    WsEvent["PlayerConnected"] = "player_connected";
    WsEvent["PlayerDisconnected"] = "player_disconnected";
    WsEvent["Error"] = "error";
    WsEvent["OpenedElsewhere"] = "opened_elsewhere";
    WsEvent["GameStarted"] = "game_started";
    WsEvent["Suggestion"] = "suggestion";
    WsEvent["DecisionSubmitted"] = "decision_submitted";
    WsEvent["PlayerSubmitted"] = "player_submitted";
    WsEvent["SetupClosed"] = "setup_closed";
    WsEvent["SetupState"] = "setup_state";
    WsEvent["TurnStarted"] = "turn_started";
    WsEvent["PlayerResponded"] = "player_responded";
    WsEvent["TurnRevealed"] = "turn_revealed";
    WsEvent["GameOver"] = "game_over";
    WsEvent["LobbyReturned"] = "lobby_returned";
    WsEvent["Reaction"] = "reaction";
    WsEvent["Connected"] = "connected";
    WsEvent["Maintenance"] = "maintenance";
})(WsEvent || (WsEvent = {}));
/**
 * Why the setup phase ended. Carried on `WsEvent.SetupClosed`.
 *
 * `Timeout` is the deadline expiring with decisions still outstanding.
 * `AllSubmitted` is every author finishing early. `PlayerGone` is the same
 * early close, reached because the only people still owing decisions left or
 * were removed — the room stops waiting for someone who is not there.
 */
export var SetupClosedReason;
(function (SetupClosedReason) {
    SetupClosedReason["Timeout"] = "timeout";
    SetupClosedReason["AllSubmitted"] = "all_submitted";
    SetupClosedReason["PlayerGone"] = "player_gone";
})(SetupClosedReason || (SetupClosedReason = {}));
/**
 * Two stages, so a deploy can drain instead of interrupting. plan.md §1.10.
 *
 * `Draining` blocks everything that would *start* something — new rooms, joins,
 * new games — while every game already running plays to its end. `Active`
 * additionally empties the lobbies. Neither state ever cuts a game off: the
 * lobby stops existing, the game does not.
 */
export var MaintenanceState;
(function (MaintenanceState) {
    MaintenanceState["Off"] = "off";
    MaintenanceState["Draining"] = "draining";
    MaintenanceState["Active"] = "active";
})(MaintenanceState || (MaintenanceState = {}));
